import { RETRACED_COMPANY_ID } from '../../../../companies/constants.js';
import type { DbClient } from '../../../../lib/db.js';
import type { AdminUserListRow } from '../../../contracts/schemas/admin/list.js';
import { buildRoleLateralJoin } from './iam-role-sql.js';
import type { RawAdminUserRow } from './types.js';

export interface ListAdminUsersParams {
  page: number;
  count: number;
  sort?: string;
  q?: string;
  filter?: string;
}

export interface ListAdminUsersDeps {
  db: DbClient;
  cdnUri: string;
}

export interface ListAdminUsersResult {
  rows: AdminUserListRow[];
  totalCount: number;
  pageSize: number;
}

/**
 * IAM-native replacement for `user_companies.role`, resolved via a `LATERAL`
 * join (`iam_role` — see {@link buildSelectAndFrom}) rather than the raw
 * column. Only feeds `role`'s `filter`/`sort` query capability now (see
 * `FILTER_COLUMNS`/`SORT_COLUMNS` below) — there is no display `role` column
 * any more. Holds the real `iam_roles.key` value (e.g. `'company-admin'`),
 * not a legacy bucket string.
 */
const IAM_ROLE_EXPR = 'iam_role.role_key';

/**
 * Resolves the most senior IAM role `users.id` holds in `user_companies.company_id`
 * (the scoped company), for `user_companies.role`'s replacement. Only valid to
 * join when `scopedToCompany` (a `user_companies` row is in scope).
 */
const ROLE_LATERAL_JOIN = buildRoleLateralJoin(
  'users.id',
  'user_companies.company_id',
  'iam_role',
);

/**
 * Filter columns accepted inside the JSON `filter` query param. Mirrors the
 * legacy `SAFE_FILTER_COL_MAP` in `backend/src/2025-06/api-service/db/users.js`.
 * All matches are case-insensitive LIKE on the lowercased column.
 *
 * `role` and `companyId` only resolve when the query is scoped to one company
 * (see `scopedToCompany` in `listAdminUsersDb`) — that's the only case where
 * `user_companies` (and, for `role`, the IAM `iam_role` lateral join — see
 * {@link buildSelectAndFrom}) is joined. `company` / `preferenceKey`
 * reference tables this query has never joined (`companies_view`,
 * `user_preferences`) — pre-existing, unrelated to the scoped/unscoped split.
 */
const FILTER_COLUMNS: Record<string, string> = {
  firstName: 'users.first_name',
  lastName: 'users.last_name',
  role: IAM_ROLE_EXPR,
  company: 'companies_view.name',
  primaryCompany: 'primary_company.name',
  email: 'users.email',
  primaryCompanyId: 'users.primary_company_id',
  companyId: 'user_companies.company_id',
  preferenceKey: 'user_preferences.key',
};

/** Filter keys that reference the `user_companies` join — only valid when scoped to one company. */
const COMPANY_SCOPED_FILTER_KEYS = new Set(['role', 'companyId']);

/** Sort columns. Mirrors legacy `SORT_MAP`. `role` is only valid when scoped to one company. */
const SORT_COLUMNS: Record<string, string> = {
  firstName: 'users.first_name',
  lastName: 'users.last_name',
  role: IAM_ROLE_EXPR,
  company: 'companies_view.name',
  primaryCompany: 'primary_company.name',
  createdAt: 'users.created_at',
  updatedAt: 'users.updated_at',
  email: 'users.email',
  isBlocked: 'users.is_blocked',
};

/** Sort keys that reference the `user_companies` join — only valid when scoped to one company. */
const COMPANY_SCOPED_SORT_KEYS = new Set(['role']);

const DEFAULT_ORDER_BY = 'users.first_name ASC';

type Binder = (value: unknown) => string;

const parseFilterJson = (
  filterJson: string | undefined,
): Record<string, unknown> => {
  if (!filterJson) return {};
  try {
    const parsed: unknown = JSON.parse(filterJson);
    return parsed && typeof parsed === 'object'
      ? (parsed as Record<string, unknown>)
      : {};
  } catch {
    return {};
  }
};

/**
 * A request is scoped to one company when its `filter.companyId` is a
 * non-empty string. Only then is `user_companies` joined, so only then do
 * `role` / `companyId` filtering and `role` sorting make sense.
 */
const isScopedToCompany = (parsedFilter: Record<string, unknown>): boolean =>
  typeof parsedFilter.companyId === 'string' && parsedFilter.companyId !== '';

const buildFilterClause = (
  parsedFilter: Record<string, unknown>,
  bind: Binder,
  scopedToCompany: boolean,
): string => {
  const clauses: string[] = [];
  for (const [key, raw] of Object.entries(parsedFilter)) {
    if (raw === undefined || raw === null || raw === '') continue;
    if (typeof raw !== 'string') continue;

    if (key === 'name') {
      clauses.push(
        `LOWER(users.first_name || ' ' || users.last_name) LIKE ${bind(`%${raw.toLowerCase()}%`)}`,
      );
      continue;
    }

    if (!scopedToCompany && COMPANY_SCOPED_FILTER_KEYS.has(key)) continue;

    const column = FILTER_COLUMNS[key];
    if (!column) continue;
    clauses.push(`LOWER(${column}) LIKE ${bind(`%${raw.toLowerCase()}%`)}`);
  }

  return clauses.length > 0 ? ` AND ${clauses.join(' AND ')}` : '';
};

const buildQClause = (q: string | undefined, bind: Binder): string => {
  if (!q) return '';
  const ph = bind(`%${q.toLowerCase()}%`);
  return ` AND (LOWER(users.last_name) LIKE ${ph} OR LOWER(users.first_name) LIKE ${ph})`;
};

const buildOrderBy = (
  sort: string | undefined,
  scopedToCompany: boolean,
): string => {
  if (!sort?.includes(':')) return DEFAULT_ORDER_BY;
  const [col, dir] = sort.split(':');
  if (!scopedToCompany && COMPANY_SCOPED_SORT_KEYS.has(col)) {
    return DEFAULT_ORDER_BY;
  }
  const direction = dir.toUpperCase() === 'DESC' ? 'DESC' : 'ASC';
  const expr = SORT_COLUMNS[col];
  return expr ? `${expr} ${direction}` : DEFAULT_ORDER_BY;
};

/**
 * `is_retraced_staff`: does this user hold any role at all in the retraced
 * staff tenant? IAM-native replacement for the old `primaryRole`-based
 * heuristic the roster used to gate the "Impersonate" action — a colleague
 * who is Retraced staff must never be impersonable from this list. Mirrors
 * the identical staff-exclusion predicate in `list-to-impersonate.ts`.
 */
const buildBaseSelectFields = (retracedTenantPh: string): string => `
  users.id,
  users.first_name,
  users.last_name,
  users.primary_company_id,
  EXISTS (
    SELECT 1 FROM iam_actor_roles iar
    JOIN iam_roles ir ON ir.id = iar.role_id
    WHERE iar.actor_type = 'user' AND iar.actor_id = users.id
      AND ir.tenant_id = ${retracedTenantPh}
  ) AS is_retraced_staff,
  users.lang_id,
  users.email,
  users.avatar_file_id,
  users.avatar_file_url,
  users.is_blocked,
  users.intercom_hash,
  users.email_sha256,
  users.updated_at,
  users.development_tag,
  users.has_agreed_with_terms,
  users.has_agreed_with_terms_at,
  (
    SELECT COUNT(uc.id)
    FROM user_companies uc
    WHERE uc.user_id = users.id
  ) AS total_user_companies,
  COUNT(users.id) OVER() AS count
`;

/**
 * Every IAM role key this user holds in the row's own company — only
 * meaningful (and only joined) when the query is scoped to one company; a
 * user can hold more than one role, so this lists all of them, unlike the
 * single most-senior role the `iam_role` lateral above resolves for
 * filter/sort.
 */
const ROLE_KEYS_LATERAL_JOIN = `
  LEFT JOIN LATERAL (
    SELECT jsonb_agg(iam_roles.key ORDER BY iam_roles.key) AS keys
      FROM iam_actor_roles
      JOIN iam_roles ON iam_roles.id = iam_actor_roles.role_id
     WHERE iam_actor_roles.actor_type = 'user'
       AND iam_actor_roles.actor_id = users.id
       AND iam_roles.tenant_id = user_companies.company_id
  ) role_keys_agg ON true
`;

/**
 * Membership columns + FROM/JOIN, split by whether the query is scoped to one
 * company. Unscoped, there is no `user_companies` row to attach to a user
 * (they may belong to several), so those columns are `NULL` and the join is
 * dropped entirely — that's what keeps the unscoped list to one row per user.
 */
const buildSelectAndFrom = (
  scopedToCompany: boolean,
  retracedTenantPh: string,
): { select: string; from: string } => {
  const baseSelectFields = buildBaseSelectFields(retracedTenantPh);
  if (scopedToCompany) {
    return {
      select: `${baseSelectFields}, user_companies.is_primary, user_companies.company_id, COALESCE(role_keys_agg.keys, '[]'::jsonb) AS role_keys`,
      // `ROLE_LATERAL_JOIN` stays even though `role` is no longer selected
      // here: `role`/`companyId` filtering and `role` sorting (FILTER_COLUMNS/
      // SORT_COLUMNS above) still reference the `iam_role` alias it provides.
      from: `FROM users LEFT JOIN user_companies ON users.id = user_companies.user_id ${ROLE_LATERAL_JOIN} ${ROLE_KEYS_LATERAL_JOIN}`,
    };
  }
  return {
    select: `${baseSelectFields}, NULL AS is_primary, NULL AS company_id, NULL AS role_keys`,
    from: 'FROM users',
  };
};

type CompanyRow = {
  id: string;
  name: string | null;
  officialName: string | null;
};

const transformRow = (
  raw: RawAdminUserRow,
  enrichments: {
    companyById: Map<string, CompanyRow>;
    primaryCompanyById: Map<string, CompanyRow>;
  },
  cdnUri: string,
): AdminUserListRow => {
  const { count: _omit, ...rest } = raw;
  const company = raw.companyId
    ? enrichments.companyById.get(raw.companyId)
    : null;
  const primaryCompany = raw.primaryCompanyId
    ? enrichments.primaryCompanyById.get(raw.primaryCompanyId)
    : null;

  return {
    ...rest,
    avatarFileUrl: raw.avatarFileUrl ? `${cdnUri}/${raw.avatarFileUrl}` : null,
    originalAvatarFileUrl: raw.avatarFileUrl ?? null,
    companyName: company?.name ?? null,
    companyOfficialName: company?.officialName ?? null,
    primaryCompanyName: primaryCompany?.name ?? null,
  };
};

const fetchCompanies = async (
  db: DbClient,
  ids: string[],
): Promise<CompanyRow[]> => {
  const unique = Array.from(new Set(ids.filter((id): id is string => !!id)));
  if (unique.length === 0) return [];
  const placeholders = unique.map((_, i) => `$${i + 1}`).join(', ');
  return db.runSql<CompanyRow>(
    `
      SELECT id, name, official_name
      FROM (
        SELECT companies_view.id, companies_view.name, companies_view.official_name FROM companies_view
        UNION ALL
        SELECT companies_removed_view.id, companies_removed_view.name, companies_removed_view.official_name FROM companies_removed_view
      ) c
      WHERE id IN (${placeholders})
    `,
    unique,
  );
};

const toCompanyMap = (rows: CompanyRow[]): Map<string, CompanyRow> => {
  const map = new Map<string, CompanyRow>();
  for (const row of rows) map.set(row.id, row);
  return map;
};

/**
 * Lists users for the admin endpoint. Two modes, chosen by whether
 * `filter.companyId` is set:
 *
 * - **Scoped to one company**: joins `user_companies` and returns that
 *   membership's `companyId` / `companyName` / `isPrimary` / `roleKeys` per
 *   row. `user_companies` has a unique `(user_id, company_id)` constraint, so
 *   this is always one row per user. Mirrors the legacy
 *   `usersService.listUnscopedWithDelegatedUsers` +
 *   `usersDB.getDelegatedUsersCompanies` in `backend/src/2025-06/api-service/`.
 * - **Unscoped** (no `companyId` filter — the admin Users roster): no join,
 *   `companyId` / `companyName` / `isPrimary` / `roleKeys` are `null` on
 *   every row. A user who belongs to several companies is exactly one row,
 *   keyed by `primaryCompanyId` / `primaryCompanyName` instead. `isRetracedStaff`
 *   stays populated either way — it's independent of company scope.
 *
 * There is no display `role`/`roleName` column any more — `role` still
 * exists as a `filter`/`sort` query capability only (see `FILTER_COLUMNS`/
 * `SORT_COLUMNS` above), backed by the `iam_role` lateral join
 * (`ROLE_LATERAL_JOIN`) which stays wired into the scoped branch below for
 * that reason alone.
 *
 * Enrichment queries (companies + primary companies) run sequentially per the
 * platform's "no Promise.all for DB" rule — legacy used Promise.all.
 */
export const listAdminUsersDb = async (
  deps: ListAdminUsersDeps,
  params: ListAdminUsersParams,
): Promise<ListAdminUsersResult> => {
  const { db, cdnUri } = deps;
  const { count, filter, page, q, sort } = params;

  const parsedFilter = parseFilterJson(filter);
  const scopedToCompany = isScopedToCompany(parsedFilter);

  const bindings: unknown[] = [];
  const bind: Binder = value => {
    bindings.push(value);
    return `$${bindings.length}`;
  };

  const filterClause = buildFilterClause(parsedFilter, bind, scopedToCompany);
  const qClause = buildQClause(q, bind);
  const orderBy = buildOrderBy(sort, scopedToCompany);
  const limitPh = bind(count);
  const offsetPh = bind(page * count);
  const retracedTenantPh = bind(RETRACED_COMPANY_ID);
  const { select, from } = buildSelectAndFrom(
    scopedToCompany,
    retracedTenantPh,
  );

  const rawRows = await db.runSql<RawAdminUserRow>(
    `
      SELECT ${select}
      ${from}
      WHERE 1 = 1
      ${filterClause}
      ${qClause}
      ORDER BY ${orderBy}
      LIMIT (${limitPh})::bigint OFFSET (${offsetPh})::bigint
    `,
    bindings,
  );

  const totalCount =
    rawRows.length > 0 && rawRows[0].count != null
      ? Number(rawRows[0].count)
      : 0;

  // Sequential enrichment — platform rule: never Promise.all for DB queries.
  // Legacy used Promise.all here; the perf impact of one extra ~5ms hop is
  // negligible vs. the consistency win.
  const companyIds = rawRows
    .map(r => r.companyId)
    .filter((id): id is string => !!id);
  const primaryCompanyIds = rawRows
    .map(r => r.primaryCompanyId)
    .filter((id): id is string => !!id);

  const companies = await fetchCompanies(db, companyIds);
  const primaryCompanies = await fetchCompanies(db, primaryCompanyIds);

  const enrichments = {
    companyById: toCompanyMap(companies),
    primaryCompanyById: toCompanyMap(primaryCompanies),
  };

  return {
    rows: rawRows.map(row => transformRow(row, enrichments, cdnUri)),
    totalCount,
    pageSize: count,
  };
};
